declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let ga4ScriptPromise: Promise<void> | null = null;

export function ga4MeasurementId(): string | undefined {
  const id = import.meta.env.VITE_GA4_MEASUREMENT_ID;
  return typeof id === 'string' && id.trim() ? id.trim() : undefined;
}

function isGa4ScriptLoaded(id: string): boolean {
  if (typeof document === 'undefined') return false;
  return Boolean(document.querySelector(`script[src*="gtag/js?id=${id}"]`));
}

export function ensureGtag(): (...args: unknown[]) => void {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  }
  return window.gtag;
}

/** تحميل gtag.js — لا يكتفي بوجود stub في index.html */
export function ensureGa4Script(measurementId?: string): Promise<void> {
  const id = measurementId || ga4MeasurementId();
  if (!id || typeof document === 'undefined') return Promise.resolve();

  if (isGa4ScriptLoaded(id)) return Promise.resolve();
  if (ga4ScriptPromise) return ga4ScriptPromise;

  ga4ScriptPromise = new Promise((resolve) => {
    const gtag = ensureGtag();
    gtag('js', new Date());

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return ga4ScriptPromise;
}

export function configureGa4(measurementId?: string): void {
  const id = measurementId || ga4MeasurementId();
  if (!id || typeof window.gtag !== 'function') return;
  window.gtag('config', id, { send_page_view: false });
}

export function fireGa4PageView(extra?: {
  page_title?: string;
  page_path?: string;
  page_location?: string;
}): void {
  const id = ga4MeasurementId();
  if (!id || typeof window.gtag !== 'function') return;

  configureGa4(id);
  window.gtag('event', 'page_view', {
    page_title: extra?.page_title ?? document.title,
    page_location: extra?.page_location ?? window.location.href,
    page_path: extra?.page_path ?? window.location.pathname,
  });
}

export function resetGa4ScriptPromise(): void {
  ga4ScriptPromise = null;
}
